import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerComponent } from '../../shared/spinner/spinner.component';
import { LookupService, MaterialLookup } from '../../services/lookup.service';
import { CisProSearchComponent } from '../cispro-search/cispro-search.component';
import { CisProSearchResult } from '../../services/cispro-search.service';
import { SlurryService, SaveSlurryRequestDto, SaveSlurryMaterialDto } from '../../services/slurry.service';

export type SlurryTab =
  | 'slurryInfo'
  | 'slurryPropertiesTests'
  | 'foamTests'
  | 'otherTests'
  | 'mixability'
  | 'redesign'
  | 'batches';

@Component({
  selector: 'app-slurry-view',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerComponent, CisProSearchComponent],
  templateUrl: './slurry-view.component.html',
  styleUrls: ['./slurry-view.component.css']
})
export class SlurryViewComponent implements OnInit {

  // ── Routing state ──────────────────────────────────────────────────────────
  slurryId!: number;
  projectId: number | null = null;
  projectName = '';

  // ── UI state ───────────────────────────────────────────────────────────────
  isLoading = false;
  isSaving = false;
  activeTab: SlurryTab = 'slurryInfo';
  isCisProSearchOpen = false;

  // ── Validation ─────────────────────────────────────────────────────────────
  blendBaseValidationError = '';
  pozSackWeightValidationErrors: { [key: number]: string } = {};

  // ── Lookup data ────────────────────────────────────────────────────────────
  liquidBaseMaterials: MaterialLookup[] = [];
  blendBaseMaterials: MaterialLookup[] = [];

  // ── Slurry Info fields ─────────────────────────────────────────────────────
  ufid = '';
  status = 'Draft';
  targetThickeningTimeFrom = '';
  targetThickeningTimeTo = '';
  slurryYield = 0.00;
  totalFluidPercent = 0.00;
  labVolumeMl = 600;
  slurryDensityPpg = 0.00;
  totalFluidGal = 0.00;
  equivalentSackLb = 0.00;
  notes1 = '';

  // ── Dry Materials ──────────────────────────────────────────────────────────
  cementSackLb = 0.00;
  private legacyEquivalentSackOverrideLb: number | null = null;
  blendCisProContainer = '';
  calculatedSG = 0.000;

  blendBaseInitial = {
    material: '',
    percent: 0 as any,
    absVolume: 0 as any,
    weightLb: 0,
    volumeGal: 0,
    labWeightG: 0,
    cisPRO: ''
  };

  blendBasePozRows: Array<{
    material: string;
    percent: any;
    absVolume: any;
    weightLb: number;
    volumeGal: number;
    labWeightG: number;
    cisPRO: string;
    pozSackWeight: any;
  }> = [];

  blendAdditives: Array<{
    additive: string;
    percent: number;
    absVolume: number;
    weightLb: number;
    volumeGal: number;
    labWeightG: number;
    byWeightOfTypeId?: number;
    cisPRO: string;
  }> = [];

  additives: Array<{
    additive: string;
    percent: number;
    absVolume: number;
    weightLb: number;
    volumeGal: number;
    labWeightG: number;
    byWeightOfTypeId?: number;
    cisPRO: string;
  }> = [];

  liquidMaterials: Array<{
    liquid: string;
    volumeGal: number;
    specificGravity: number;
    weightLb: number;
    labWeightG: number;
    cisPRO: string;
  }> = [{
    liquid: '',
    volumeGal: 0.000,
    specificGravity: 0.820,
    weightLb: 0.000,
    labWeightG: 0.00,
    cisPRO: ''
  }];

  liquidAdditives: Array<{
    additive: string;
    volumeGal: number;
    specificGravity: number;
    weightLb: number;
    labWeightG: number;
    cisPRO: string;
  }> = [];

  // ── Material Constants ─────────────────────────────────────────────────────
  private readonly WATER_DENSITY = 8.34;
  private readonly LB_PER_GAL_IN_G_PER_ML = 0.119826;
  private readonly GAL_IN_FT_CUBICLE = 7.48052;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private lookupService: LookupService,
    private slurryService: SlurryService
  ) {}

  ngOnInit(): void {
    // Read slurryId from route
    this.route.paramMap.subscribe(params => {
      const id = params.get('slurryId');
      if (id) {
        this.slurryId = +id;
        this.ufid = `S-${this.slurryId}`;
      }
    });

    // Store navState reference before async call
    const navState = history.state as {
      // Scalars
      projectId?:               number;
      projectName?:             string;
      ufSlurryId?:              string;
      slurryDensity?:           number;
      labVolumeMl?:             number;
      slurryYield?:             number;
      totalFluidPercent?:       number;
      totalFluidGal?:           number;
      equivalentSackWeight?:    number;
      notes?:                   string;
      cementSackWeight?:        number;
      blendCisProContainerId?:  string;
      calculatedSg?:            number;
      targetThickeningTimeFrom?: string;
      targetThickeningTimeTo?:   string;
      // Material rows
      blendBaseInitial?: {
        material: string; percent: number; absVolume: number;
        weightLb: number; volumeGal: number; labWeightG: number; cisPRO: string;
      };
      blendBasePozRows?: Array<{
        material: string; percent: number; absVolume: number;
        weightLb: number; volumeGal: number; labWeightG: number;
        cisPRO: string; pozSackWeight: number;
      }>;
      blendAdditives?: Array<{
        additive: string; percent: number; absVolume: number;
        weightLb: number; volumeGal: number; labWeightG: number; byWeightOfTypeId?: number; cisPRO: string;
      }>;
      additives?: Array<{
        additive: string; percent: number; absVolume: number;
        weightLb: number; volumeGal: number; labWeightG: number; byWeightOfTypeId?: number; cisPRO: string;
      }>;
      liquidMaterials?: Array<{
        liquid: string; volumeGal: number; specificGravity: number;
        weightLb: number; labWeightG: number; cisPRO: string;
      }>;
      liquidAdditives?: Array<{
        additive: string; volumeGal: number; specificGravity: number;
        weightLb: number; labWeightG: number; cisPRO: string;
      }>;
    };

    // Load materials first, then restore state after materials are loaded
    this.loadGetSlurryMaterials(() => {
      // ── Restore all state passed from slurry-details createSlurry() ──────────
      // ── Scalars ───────────────────────────────────────────────────────────────
      if (navState?.projectId != null)              { this.projectId = navState.projectId; }
      if (navState?.projectName)                    { this.projectName = navState.projectName; }
      if (navState?.ufSlurryId)                     { this.ufid = navState.ufSlurryId; }
      if (navState?.slurryDensity != null)          { this.slurryDensityPpg = navState.slurryDensity; }
      if (navState?.labVolumeMl != null)            { this.labVolumeMl = navState.labVolumeMl; }
      if (navState?.slurryYield != null)            { this.slurryYield = navState.slurryYield; }
      if (navState?.totalFluidPercent != null)      { this.totalFluidPercent = navState.totalFluidPercent; }
      if (navState?.totalFluidGal != null)          { this.totalFluidGal = navState.totalFluidGal; }
      if (navState?.equivalentSackWeight != null)   { this.equivalentSackLb = navState.equivalentSackWeight; }
      if (navState?.notes != null)                  { this.notes1 = navState.notes; }
      if (navState?.cementSackWeight != null)       { this.cementSackLb = navState.cementSackWeight; }
      this.legacyEquivalentSackOverrideLb =
      navState?.cementSackWeight == null && (navState?.equivalentSackWeight ?? 0) > 0
        ? navState?.equivalentSackWeight ?? null
        : null;
    if (navState?.blendCisProContainerId)         { this.blendCisProContainer = navState.blendCisProContainerId; }
      if (navState?.calculatedSg != null)           { this.calculatedSG = navState.calculatedSg; }
      if (navState?.targetThickeningTimeFrom != null) { this.targetThickeningTimeFrom = navState.targetThickeningTimeFrom; }
      if (navState?.targetThickeningTimeTo != null)   { this.targetThickeningTimeTo = navState.targetThickeningTimeTo; }

      // ── Material rows ─────────────────────────────────────────────────────────
      if (navState?.blendBaseInitial) {
        this.blendBaseInitial = { ...navState.blendBaseInitial };
      }
      if (navState?.blendBasePozRows?.length) {
        this.blendBasePozRows = navState.blendBasePozRows.map(r => ({ ...r }));
      }
      if (navState?.blendAdditives?.length) {
        this.blendAdditives = navState.blendAdditives.map(r => ({ ...r }));
      }
      if (navState?.additives?.length) {
        this.additives = navState.additives.map(r => ({ ...r }));
      }
      if (navState?.liquidMaterials?.length) {
        this.liquidMaterials = navState.liquidMaterials.map(r => ({ ...r }));
      }
      if (navState?.liquidAdditives?.length) {
        this.liquidAdditives = navState.liquidAdditives.map(r => ({ ...r }));
      }
    });
  }

  // ── Tab navigation ─────────────────────────────────────────────────────────
  setTab(tab: SlurryTab): void {
    this.activeTab = tab;
  }

  // ── Lookups ────────────────────────────────────────────────────────────────
  loadGetSlurryMaterials(callback?: () => void): void {
    this.lookupService.getSlurryMaterial().subscribe({
      next: (materials) => {
        this.liquidBaseMaterials = materials.filter(m => m.materialTypeId === 4);
        this.blendBaseMaterials  = materials.filter(m => m.materialTypeId === 3 || m.materialTypeId === 5);
        // Call the callback after materials are loaded
        if (callback) {
          callback();
        }
      },
      error: () => {
        this.liquidBaseMaterials = [];
        this.blendBaseMaterials  = [];
        // Still call callback even on error so the app doesn't hang
        if (callback) {
          callback();
        }
      }
    });
  }

  // ── Display formatting helpers ─────────────────────────────────────────────
  fmt(value: number, decimals: number): string {
    return (value ?? 0).toFixed(decimals);
  }

  getSelectedMaterialName(materialId: string): string {
    const material = this.blendBaseMaterials.find(m => m.id === Number(materialId));
    return material ? material.name : '';
  }

  /**
   * Get available pozzolan materials for a specific row (excluding already selected materials)
   */
  getAvailablePozMaterials(currentRowIndex?: number): MaterialLookup[] {
    if (!this.blendBaseMaterials || this.blendBaseMaterials.length === 0) {
      return [];
    }

    // Get IDs of materials already selected in other poz rows
    const selectedIds = new Set<number>();
    
    // Add cement base material ID to exclude it
    if (this.blendBaseInitial.material) {
      selectedIds.add(+this.blendBaseInitial.material);
    }

    // Add IDs from other poz rows (excluding current row being edited)
    this.blendBasePozRows.forEach((row, index) => {
      if (index !== currentRowIndex && row.material) {
        selectedIds.add(+row.material);
      }
    });

    // Filter to only pozzolan materials (materialTypeId === 5) and exclude already selected
    const available = this.blendBaseMaterials.filter(m => m.materialTypeId === 5 && !selectedIds.has(m.id));

    // If this is an existing row with a selected material, make sure it's included
    if (currentRowIndex !== undefined && this.blendBasePozRows[currentRowIndex]?.material) {
      const currentMaterialId = +this.blendBasePozRows[currentRowIndex].material;
      const currentMaterial = this.blendBaseMaterials.find(m => m.id === currentMaterialId);
      if (currentMaterial && !available.some(m => m.id === currentMaterialId)) {
        available.push(currentMaterial);
      }
    }

    return available.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  private round(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  private n(value: unknown): number {
    const parsed = typeof value === 'number' ? value : parseFloat(`${value ?? ''}`);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  // ── Formatting methods ─────────────────────────────────────────────────────
  selectAllOnFocus(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    input.select();
  }

  formatPozSackWeightOnBlur(index: number, event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBasePozRows[index].pozSackWeight = value.toFixed(2);
    } else if (input.value === '') {
      this.blendBasePozRows[index].pozSackWeight = '';
    }
  }

  formatPozPercentOnBlur(index: number, event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBasePozRows[index].percent = value.toFixed(3);
    } else if (input.value === '') {
      this.blendBasePozRows[index].percent = '0.000';
    }
  }

  formatInitialPercentOnBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBaseInitial.percent = value.toFixed(3);
    } else if (input.value === '') {
      this.blendBaseInitial.percent = '0.000';
    }
  }

  formatPozAbsVolumeOnBlur(index: number, event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBasePozRows[index].absVolume = value.toFixed(4);
    } else if (input.value === '') {
      this.blendBasePozRows[index].absVolume = '0.0000';
    }
  }

  formatInitialAbsVolumeOnBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    if (!isNaN(value)) {
      this.blendBaseInitial.absVolume = value.toFixed(4);
    } else if (input.value === '') {
      this.blendBaseInitial.absVolume = '0.0000';
    }
  }
  
  // ── Validation ─────────────────────────────────────────────────────────────
  validateBlendBase(): void {
    // Calculate total blend base percent
    const initialPercent = this.n(this.blendBaseInitial.percent);
    const pozTotalPercent = this.blendBasePozRows.reduce((sum, row) => sum + this.n(row.percent), 0);
    const totalPercent = initialPercent + pozTotalPercent;

    // Check if slurry base is required and not greater than 100%
    if (initialPercent === 0) {
      this.blendBaseValidationError = 'Slurry base is required and can\'t be greater then 100%';
    } else if (totalPercent !== 100) {
      this.blendBaseValidationError = 'Total dry base should be exactly 100%';
    } else {
      this.blendBaseValidationError = '';
    }
  }

  validatePozSackWeight(index: number): void {
    const pozRow = this.blendBasePozRows[index];
    if (pozRow.material && (!pozRow.pozSackWeight || pozRow.pozSackWeight === 0)) {
      this.pozSackWeightValidationErrors[index] = 'Value is required';
    } else {
      delete this.pozSackWeightValidationErrors[index];
    }
  }

  validateAllPozSackWeights(): void {
    this.blendBasePozRows.forEach((row, index) => {
      if (row.material) {
        this.validatePozSackWeight(index);
      }
    });
  }

  private getEffectiveCementSackLb(): number {
    return this.legacyEquivalentSackOverrideLb ?? this.cementSackLb;
  }

  private clearLegacyEquivalentSackOverride(): void {
    this.legacyEquivalentSackOverrideLb = null;
  }

  onCementSackWeightChange(): void {
    this.clearLegacyEquivalentSackOverride();
    this.recalculate();
  }

  // ── Recalculate (same chain as slurry-details) ─────────────────────────────
  recalculate(): void {
    // Validate blend base percentages
    this.validateBlendBase();
    // Validate all poz sack weights
    this.validateAllPozSackWeights();
    
    const effectiveCementSackLb = this.getEffectiveCementSackLb();
    const cementDensity = this.densityFromAbsVolume(this.blendBaseInitial.absVolume);
    this.blendBaseInitial.weightLb = this.round((effectiveCementSackLb / 100) * (this.blendBaseInitial.percent || 0), 3);
    this.blendBaseInitial.volumeGal = cementDensity > 0
      ? this.round(this.blendBaseInitial.weightLb / cementDensity, 3) : 0;

    for (const poz of this.blendBasePozRows) {
      const d = this.densityFromAbsVolume(poz.absVolume);
      poz.weightLb  = this.round(((poz.pozSackWeight || 0) / 100) * (poz.percent || 0), 3);
      poz.volumeGal = d > 0 ? this.round(poz.weightLb / d, 3) : 0;
    }

    const cementWeight   = this.blendBaseInitial.weightLb;
    const pozTotalWeight = this.blendBasePozRows.reduce((s, p) => s + (p.weightLb || 0), 0);
    this.equivalentSackLb = this.legacyEquivalentSackOverrideLb ?? this.round(cementWeight + pozTotalWeight, 2);

    this.computeDryAdditiveValues(this.blendAdditives);
    this.computeDryAdditiveValues(this.additives);

    for (const liq of this.liquidAdditives) {
      liq.weightLb = this.round((liq.specificGravity || 0) * this.WATER_DENSITY * (liq.volumeGal || 0), 3);
    }

    this.computeLiquidBaseVolume();
    this.computeAllLabWeights();
    this.computeSummaryFields();
  }

  private computeDryAdditiveValues(
    additives: Array<{ percent: number; absVolume: number; weightLb: number; volumeGal: number; byWeightOfTypeId?: number }>
  ): void {
    for (const add of additives) {
      const density = this.densityFromAbsVolume(add.absVolume);
      const bwot = add.byWeightOfTypeId ?? 1;
      add.weightLb = bwot === 2
        ? this.round((this.getLiquidBaseWeight() / 100) * (add.percent || 0), 3)
        : this.round((this.equivalentSackLb / 100) * (add.percent || 0), 3);
      add.volumeGal = density > 0 ? this.round(add.weightLb / density, 3) : 0;
    }
  }

  private computeLiquidBaseVolume(): void {
    if (!this.liquidMaterials.length || this.slurryDensityPpg <= 0) return;
    const lb  = this.liquidMaterials[0];
    const sgb = lb.specificGravity || 0;
    if (sgb <= 0) return;
    const dryWt   = this.getDryTotalWeight();
    const dryVol  = this.getDryTotalVolume();
    const liqAddWt  = this.liquidAdditives.reduce((s, a) => s + (a.weightLb || 0), 0);
    const liqAddVol = this.liquidAdditives.reduce((s, a) => s + (a.volumeGal || 0), 0);
    const denom = this.slurryDensityPpg - this.WATER_DENSITY * sgb;
    if (denom === 0) return;
    let vol = (dryWt + liqAddWt - this.slurryDensityPpg * (dryVol + liqAddVol)) / denom;
    if (vol < 0) vol = 0;
    lb.volumeGal = this.round(vol, 3);
    lb.weightLb  = this.round(sgb * this.WATER_DENSITY * lb.volumeGal, 3);
  }

  private computeAllLabWeights(): void {
    const totalWeight = this.getTotalWeight();
    if (totalWeight <= 0) { this.clearAllLabWeights(); return; }
    const factor = (this.slurryDensityPpg * this.labVolumeMl * this.LB_PER_GAL_IN_G_PER_ML) / totalWeight;
    this.blendBaseInitial.labWeightG = this.round(this.blendBaseInitial.weightLb * factor, 2);
    for (const p of this.blendBasePozRows)  p.labWeightG = this.round(p.weightLb * factor, 2);
    for (const a of [...this.blendAdditives, ...this.additives]) {
      if ((a.byWeightOfTypeId ?? 1) !== 2) {
        a.labWeightG = this.round(a.weightLb * factor, 2);
      }
    }
    if (this.liquidMaterials.length > 0)
      this.liquidMaterials[0].labWeightG = this.round(this.liquidMaterials[0].weightLb * factor, 2);
    for (const l of this.liquidAdditives) l.labWeightG = this.round(l.weightLb * factor, 2);
    for (let i = 1; i < this.liquidMaterials.length; i++)
      this.liquidMaterials[i].labWeightG = this.round(this.liquidMaterials[i].weightLb * factor, 2);
    const liquidBaseLabWeight = this.liquidMaterials.reduce((sum, liquid) => sum + (liquid.labWeightG || 0), 0);
    for (const a of [...this.blendAdditives, ...this.additives]) {
      if ((a.byWeightOfTypeId ?? 1) === 2) {
        a.labWeightG = this.round((liquidBaseLabWeight / 100) * (a.percent || 0), 2);
      }
    }
  }

  private clearAllLabWeights(): void {
    this.blendBaseInitial.labWeightG = 0;
    this.blendBasePozRows.forEach(p => p.labWeightG = 0);
    this.blendAdditives.forEach(a => a.labWeightG = 0);
    this.additives.forEach(a => a.labWeightG = 0);
    this.liquidMaterials.forEach(l => l.labWeightG = 0);
    this.liquidAdditives.forEach(l => l.labWeightG = 0);
  }

  private computeSummaryFields(): void {
    this.slurryYield       = this.round(this.getTotalVolume() / this.GAL_IN_FT_CUBICLE, 2);
    const liqWt = this.getLiquidTotalWeight();
    this.totalFluidPercent = this.equivalentSackLb > 0
      ? this.round(100 * liqWt / this.equivalentSackLb, 2) : 0;
    this.totalFluidGal     = this.round(this.getLiquidTotalVolume(), 2);
    const pdw = this.getPrimaryDryTotalWeight();
    const pdv = this.getPrimaryDryTotalVolume();
    this.calculatedSG      = pdv > 0 ? this.round((pdw / pdv) / 8.33, 3) : 0;
  }

  private densityFromAbsVolume(av: number): number { return av > 0 ? 1 / av : 0; }
  private getDryTotalWeight(): number {
    return (this.blendBaseInitial.weightLb || 0)
      + this.blendBasePozRows.reduce((s,p)=>s+(p.weightLb||0),0)
      + this.blendAdditives.reduce((s,a)=>s+(a.weightLb||0),0)
      + this.additives.reduce((s,a)=>s+(a.weightLb||0),0);
  }
  private getDryTotalVolume(): number {
    return (this.blendBaseInitial.volumeGal || 0)
      + this.blendBasePozRows.reduce((s,p)=>s+(p.volumeGal||0),0)
      + this.blendAdditives.reduce((s,a)=>s+(a.volumeGal||0),0)
      + this.additives.reduce((s,a)=>s+(a.volumeGal||0),0);
  }
  private getPrimaryDryTotalWeight(): number {
    return (this.blendBaseInitial.weightLb||0)
      + this.blendBasePozRows.reduce((s,p)=>s+(p.weightLb||0),0)
      + this.blendAdditives.reduce((s,a)=>s+(a.weightLb||0),0);
  }
  private getPrimaryDryTotalVolume(): number {
    return (this.blendBaseInitial.volumeGal||0)
      + this.blendBasePozRows.reduce((s,p)=>s+(p.volumeGal||0),0)
      + this.blendAdditives.reduce((s,a)=>s+(a.volumeGal||0),0);
  }
  private getLiquidTotalWeight(): number {
    return this.liquidMaterials.reduce((s,l)=>s+(l.weightLb||0),0)
      + this.liquidAdditives.reduce((s,l)=>s+(l.weightLb||0),0);
  }
  private getLiquidTotalVolume(): number {
    return this.liquidMaterials.reduce((s,l)=>s+(l.volumeGal||0),0)
      + this.liquidAdditives.reduce((s,l)=>s+(l.volumeGal||0),0);
  }
  private getLiquidBaseWeight(): number {
    return this.liquidMaterials.length > 0 ? (this.liquidMaterials[0].weightLb||0) : 0;
  }
  private getTotalWeight(): number { return this.getDryTotalWeight() + this.getLiquidTotalWeight(); }
  private getTotalVolume(): number { return this.getDryTotalVolume() + this.getLiquidTotalVolume(); }

  // ── Material change handlers ───────────────────────────────────────────────
  onBlendBaseMaterialChange(index?: number): void {
    const materialId = index === undefined ? +this.blendBaseInitial.material : +this.blendBasePozRows[index].material;
    if (!materialId) {
      if (index === undefined) this.blendBaseInitial.absVolume = 0;
      else this.blendBasePozRows[index].absVolume = 0;
      this.recalculate(); return;
    }
    const absVol     = this.blendBaseMaterials.find(m => m.id === materialId)?.absoluteVolume ?? 0;
    const sackWeight = this.blendBaseMaterials.find(m => m.id === materialId)?.sackWeight ?? 0;
    if (index === undefined) {
      this.blendBaseInitial.absVolume = +absVol;
      if (sackWeight > 0) {
        this.clearLegacyEquivalentSackOverride();
      }
      this.cementSackLb = +sackWeight;
    } else {
      this.blendBasePozRows[index].absVolume     = +absVol;
      this.blendBasePozRows[index].pozSackWeight = +sackWeight;
    }
    this.recalculate();
  }

  onLiquidMaterialChange(index: number): void {
    const selectedId = +this.liquidMaterials[index].liquid;
    if (!selectedId) return;
    this.liquidMaterials[index].specificGravity =
      +(this.liquidBaseMaterials.find(m => m.id === selectedId)?.specificGravity ?? 0);
    this.recalculate();
  }

  onAbsoluteVolumeChange(): void { this.recalculate(); }

  // ── Poz rows ───────────────────────────────────────────────────────────────
  /**
   * Check if we can add more pozzolan rows (maximum 4)
   */
  get canAddPoz(): boolean {
    return this.blendBasePozRows.length < 4;
  }

  addBlendBasePoz(): void {
    // Maximum 4 pozzolan materials allowed
    if (this.blendBasePozRows.length >= 4) {
      return;
    }
    this.blendBasePozRows.push({ material:'', percent:0, absVolume:0, weightLb:0,
      volumeGal:0, labWeightG:0, cisPRO:'', pozSackWeight:'' });
  }
  
  removeBlendBasePoz(index: number): void {
    const row = this.blendBasePozRows[index];
    
    // If no material is selected, remove the row completely
    if (!row.material) {
      this.blendBasePozRows.splice(index, 1);
      this.recalculate();
      return;
    }
    
    // If material is selected, keep the dropdown value and absVolume but clear all other inputs
    // and move the row to the end
    const materialId = row.material;
    const absVolume = row.absVolume;
    
    // Remove from current position
    this.blendBasePozRows.splice(index, 1);
    
    // Add to the end with cleared values but preserve material and absVolume
    this.blendBasePozRows.push({
      material: materialId,
      percent: 0,
      absVolume: absVolume,
      weightLb: 0,
      volumeGal: 0,
      labWeightG: 0,
      cisPRO: '',
      pozSackWeight: '' // Empty field after reset
    });
    
    this.recalculate();
  }

  addLiquidMaterial(): void {
    this.liquidMaterials.push({ liquid:'', volumeGal:0, specificGravity:0, weightLb:0, labWeightG:0, cisPRO:'' });
  }
  addLiquidAdditive(): void {
    this.liquidAdditives.push({ additive:'', volumeGal:0, specificGravity:0, weightLb:0, labWeightG:0, cisPRO:'' });
  }

  // ── CisPRO Search ──────────────────────────────────────────────────────────
  openCisProSearch(): void  { this.isCisProSearchOpen = true; }
  closeCisProSearch(): void { this.isCisProSearchOpen = false; }
  onCisProSelect(result: CisProSearchResult): void {
    this.blendCisProContainer = result.barcodeId;
    this.isCisProSearchOpen = false;
  }

  // ── Action buttons ─────────────────────────────────────────────────────────

  /** Update: re-save the slurry data (same payload, future PUT call). */
  update(): void {
    console.log('Update slurry', this.slurryId);
    // TODO: call PUT /api/v1/slurry/{slurryId} once update endpoint exists
    alert('Update functionality will be implemented soon.');
  }

  /** Send To Lab: change status → SentToLab (future PATCH call). */
  sendToLab(): void {
    console.log('Send To Lab', this.slurryId);
    // TODO: call PATCH /api/v1/slurry/{slurryId}/send-to-lab
    alert('Send To Lab functionality will be implemented soon.');
  }

  /** Complete Slurry: change status → Completed (future PATCH call). */
  completeSlurry(): void {
    console.log('Complete Slurry', this.slurryId);
    // TODO: call PATCH /api/v1/slurry/{slurryId}/complete
    alert('Complete Slurry functionality will be implemented soon.');
  }

  /** Breadcrumb back to project details. */
  goToProjectDetails(): void {
    if (this.projectId) {
      this.router.navigate(['/projects-dashboard'], {
        state: { openProjectId: this.projectId }
      });
    } else {
      this.router.navigate(['/projects-dashboard']);
    }
  }
}
